import { LocationStrategy, PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { logDiagnostic } from '@office-start/telemetry-service';
import { KnownDiagnosticInstrumentationEvents } from '@office-start/telemetry-types';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { OfficeAuxiliaryRoute, OfficeNamedOutlet, OfficeRoute } from '../constants/office-route.enum';
import { RoutePaths } from '../constants/route-paths.constant';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FileItem, FileType } from '../models/file-item.model';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { UrlBuilder } from '../utilities/url-builder.utility';
import { LocalStorageService } from './local-storage.service';
import { ShellShimService } from './shell-shim.service';
import { WindowService } from './window.service';

// Provides context of a route, including both the route used and whether there is app context (as in the case of Start or Templates pages)
// NOTE: If updating in a way that would introduce breaking changes, please update the officeRouteStateVersion appropriately
export type OfficeRouteItem = BaseOfficeRouteItem<Exclude<OfficeRoute, OfficeRoute.M365Apps>> | MOSOfficeRouteItem;

export interface BaseOfficeRouteItem<T extends OfficeRoute> {
    route: T;
    app?: FileType;
}

export interface MOSOfficeRouteItem extends BaseOfficeRouteItem<OfficeRoute.M365Apps> {
    appId?: string;
    entityId?: string;
    subEntityId?: string;
}

export interface RouteChangeEvent {
    type: RouteChangeEventType;
    currentRoute: OfficeRouteItem;
}

export type RouteChangeStartEvent = RouteChangeEvent;

export interface RouteChangeEndEvent extends RouteChangeEvent {
    previousRoute: OfficeRouteItem;
    backRoute: OfficeRouteItem;
}

export const enum RouteChangeEventType {
    NavigationStart,
    NavigationEnd
}

// This model is stored in browser history and read on page navigation
// NOTE: If updating in a way that would introduce breaking changes, please update the officeRouteStateVersion appropriately
interface OfficeRouteHistoryEntry {
    version: number;
    routeItem: OfficeRouteItem;
    index: number;
    backRouteItem: OfficeRouteItem;
    backIndex: number;
}

@Injectable({
    providedIn: 'root'
})
export class OfficeRouteService {
    private currentRouteState: OfficeRouteHistoryEntry;
    private previousRouteState: OfficeRouteHistoryEntry;
    private readonly _routes: BehaviorSubject<RouteChangeEvent>;
    private lastPoppedState: OfficeRouteHistoryEntry;
    private versionMismatch: boolean;
    private rewriteInProgress: boolean;
    private ignoreNextNavigation: boolean;
    private readonly initialRoute: OfficeRouteItem;
    private readonly nativeSearchEnabled: boolean;

    private static readonly officeRouteStateVersion = 1;

    // Consequences of historyEnabled = false:
    //     OfficeHome back button will be hidden from App Gallery page
    //     Hitting OfficeHome back button on search page will always lead to home page regardless of which page search was launched from
    //     OfficeHome back button acts as a link rather than browser back button, adding a new state to the history stack rather than removing one
    private historyEnabled: boolean;

    private readonly routes: Observable<RouteChangeEvent>;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly instrumentationService: InstrumentationService,
        private readonly localStorageService: LocalStorageService,
        private readonly locationStrategy: LocationStrategy,
        private readonly platformLocation: PlatformLocation,
        private readonly router: Router,
        private readonly windowService: WindowService,
        private readonly shellShimService: ShellShimService
    ) {
        const history = this.windowService.nativeWindow.history;
        this.historyEnabled = !!history && !!history.go;
        if (this.historyEnabled) {
            // Accessing history.state in IE can throw an exception
            try {
                this.lastPoppedState = this.parseState(history.state);
                // Necessary to keep state when angular changes locations (https://github.com/angular/angular/issues/16911)
                // If this is resolved, we can just check history.state navigation subscription
                this.windowService.nativeWindow.addEventListener('popstate', (pop: PopStateEvent) => {
                    this.lastPoppedState = this.parseState(pop.state);
                });
            } catch (_e) {
                this.historyEnabled = false;
            }
        }

        if (!this.historyEnabled) {
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.HistoryDisabled);
            logDiagnostic({ eventName: KnownDiagnosticInstrumentationEvents.HistoryDisabled, params: {}});
        }

        // This is a performance optimization to use the platform location and pathname to determine the initial URL
        // Router initialization takes extra time and we don't want to wait for that to render the App Tiles and other Hero content
        this.initialRoute = this.currentRouteFromPath(this.platformLocation.pathname);
        this._routes = new BehaviorSubject<RouteChangeEvent>({ currentRoute: this.initialRoute, type: RouteChangeEventType.NavigationStart });
        this.routes = this._routes.asObservable().pipe(
            distinctUntilChanged((a: RouteChangeEvent, b: RouteChangeEvent) => a.type === b.type && OfficeRouteService.areRoutesEqual(a.currentRoute, b.currentRoute))
        );
        this.nativeSearchEnabled = this.capabilitiesService.capabilities.nativeSearchInHeaderEnabled;
    }

    public static areRoutesEqual(route1: OfficeRouteItem, route2: OfficeRouteItem): boolean {
        if (!route1 || !route2 || (route1.route !== route2.route)) {
            return false;
        }
        if (route1.route === OfficeRoute.M365Apps && route2.route === OfficeRoute.M365Apps) {
            return route1.appId === route2.appId && route1.entityId === route2.entityId && route1.subEntityId === route2.subEntityId;
        }
        return route1.app === route2.app;
    }

    public start(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationStart || event instanceof NavigationEnd)
        ).subscribe((e: NavigationStart | NavigationEnd) => {
            const path = e.url ? e.url.split('?')[0] : null;
            const officeRoute = this.currentRouteFromPath(path);
            const routeChangeEvent: RouteChangeEvent = { currentRoute: officeRoute, type: e instanceof NavigationStart ? RouteChangeEventType.NavigationStart : RouteChangeEventType.NavigationEnd };

            const updateObservable = !this.ignoreNextNavigation && (!this.currentRouteState || !OfficeRouteService.areRoutesEqual(officeRoute, this.currentRouteState.routeItem) || this.rewriteInProgress);

            if (this.isRouteChangeEndEvent(routeChangeEvent)) {
                // If we eagerly loaded a route reset it to the default of 'deferred'
                if (this.router.urlUpdateStrategy === 'eager') {
                    this.router.urlUpdateStrategy = 'deferred';
                }

                if (this.ignoreNextNavigation) {
                    this.ignoreNextNavigation = false;
                    return;
                }

                let newRouteState: OfficeRouteHistoryEntry = {
                    routeItem: officeRoute,
                    index: this.historyEnabled ? this.windowService.nativeWindow.history.length : -1,
                    backRouteItem: null,
                    backIndex: -1,
                    version: OfficeRouteService.officeRouteStateVersion
                };
                // If we have a state from previous page loads, then use it
                if (this.lastPoppedState && OfficeRouteService.areRoutesEqual(this.lastPoppedState.routeItem, officeRoute)) {
                    newRouteState = this.lastPoppedState;
                } else if (this.historyEnabled && this.currentRouteState) {
                    // We should only be missing lastPoppedState when a new history entry is added (user is moving forward to new history entry on same or different route)
                    // Known Issues:
                    //     1. Chrome for iOS sometimes doesn't update the history length on pushstate (https://bugs.chromium.org/p/chromium/issues/detail?id=783382#c3)
                    //     2. Some browser versions have been known to lose state on navigation between domains, so we would end up losing state when user navigate to Office via browser back
                    //        This will also happen if our state version changes in the middle of a browser session
                    //     3. When office.com is loaded via replace we won't have a lastPoppedState, but current index will not equal history length (happens in PWA when switching accounts)
                    if (newRouteState.index !== this.currentRouteState.index + 1) {
                        const params = {};
                        params[InstrumentationParam.Index] = newRouteState.index.toString();
                        if (officeRoute) {
                            params[InstrumentationParam.State] = officeRoute.route;
                            params[InstrumentationParam.DocumentType] = officeRoute.app ? FileType[officeRoute.app] : '';
                        }
                        params[InstrumentationParam.Error] = this.versionMismatch ? 'Version' : 'Index';
                        params[InstrumentationParam.DebugInfo] = `new: ${JSON.stringify(newRouteState)}, current: ${JSON.stringify(this.currentRouteState)}, previous: ${JSON.stringify(this.previousRouteState)}`;
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.UnexpectedHistoryState, params);
                        logDiagnostic({
                            eventName: KnownDiagnosticInstrumentationEvents.UnexpectedHistoryState,
                            params
                        });

                        // The first issue above can be fixed by explicitly setting newRouteState.index = currentRouteState.index + 1, so we attempt to rule out the 2nd by checking if first route was home page
                        if (!this.previousRouteState && !(this.currentRouteState.routeItem && this.currentRouteState.routeItem.route === OfficeRoute.OfficeHome)) {
                            this.historyEnabled = false;
                            newRouteState.index = -1;
                        }
                    }

                    if (this.historyEnabled) {
                        newRouteState.index = this.currentRouteState.index + 1;
                        const sameRoute = OfficeRouteService.areRoutesEqual(this.currentRouteState.routeItem, officeRoute);
                        newRouteState.backRouteItem = sameRoute ? this.currentRouteState.backRouteItem : this.currentRouteState.routeItem;
                        newRouteState.backIndex = sameRoute ? this.currentRouteState.backIndex : this.currentRouteState.index;
                    }
                }
                this.updateState(newRouteState);
                this.lastPoppedState = null;
                this.previousRouteState = this.currentRouteState;
                this.currentRouteState = newRouteState;
                this.rewriteInProgress = false;

                routeChangeEvent.previousRoute = this.previousRouteState ? this.previousRouteState.routeItem : null;
                routeChangeEvent.backRoute = newRouteState.backRouteItem;
            }

            if (updateObservable) {
                this._routes.next(routeChangeEvent);
            }
        });
    }

    public hasNavigated(): boolean {
        return this.router.navigated;
    }

    public back(defaultRoute: OfficeRouteItem = null) {
        if (!this.currentRouteState) {
            if (defaultRoute) {
                this.navigateTo(defaultRoute);
            }
            return;
        }

        if (this.nativeSearchEnabled && this.currentRouteState.routeItem && this.currentRouteState.routeItem.route === OfficeRoute.Search) {
            this.shellShimService.getShellShim().then(shellShim => {
                shellShim.Search.SetSearchText('');
            });
        }

        const routeDiff = this.currentRouteState.backIndex - this.currentRouteState.index;
        if (this.historyEnabled && this.currentRouteState.backIndex >= 0 && routeDiff < 0) {
            this.windowService.nativeWindow.history.go(routeDiff);
        } else if (defaultRoute) {
            this.navigateTo(defaultRoute);
        } else {
            this.platformLocation.back();
        }
    }

    public getNavigationUrl(path: string = '/', ...routeParams: Array<any>): string {
        const commands = [path].concat(routeParams);
        const queryParams = { ...this.activatedRoute.snapshot.queryParams };
        return this.router.serializeUrl(this.router.createUrlTree(commands, { queryParams }));
    }

    public navigateTo(route: OfficeRouteItem, replace: boolean = false, additionalParams: { [x: string]: any } = null, localStorageKey?: string, eagerLoad = false): Promise<boolean> {
        const commandsAndParams = this.getCommandsAndParamsForRoute(route, additionalParams);

        if (!commandsAndParams) {
            return;
        }

        const [commands, queryParams] = commandsAndParams;
        return this.navigateToInternal(commands, replace, queryParams, localStorageKey, eagerLoad);
    }

    public getUrlForRoute(route: OfficeRouteItem, additionalParams: { [x: string]: any } = null): string {
        const commandsAndParams = this.getCommandsAndParamsForRoute(route, additionalParams);
        if (!commandsAndParams) {
            return;
        }
        const [commands, params] = commandsAndParams;
        const base = `${this.platformLocation.protocol}//${this.platformLocation.hostname}/`;
        const path = commands.reduce((acc, command) => acc + command.split('/').filter(seg => !!seg).join('/') + '/', base);
        return UrlBuilder.appendQueryParams(path, params);
    }

    public updateQueryParams(queryParams: Params) {
        this.ignoreNextNavigation = true;
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true
        }).then(() => {
            this.ignoreNextNavigation = false;
        }).catch(() => {
            this.ignoreNextNavigation = false;
        });
    }

    public navigateToAuxilaryRoute(outlet: OfficeNamedOutlet, route: OfficeAuxiliaryRoute) {
        const outlets = {};
        outlets[outlet] = route ? [route] : null;
        this.ignoreNextNavigation = true;
        this.router.navigate([{ outlets }], { queryParamsHandling: 'preserve', skipLocationChange: true });
    }

    public redirectAndRewrite(path: string = '/', ...params: Array<any>): void {
        this.currentRouteState = this.previousRouteState;
        this.rewriteInProgress = true;
        const commands = [path].concat(params);
        if (this.capabilitiesService.capabilities.isEmbedMode) {
            const firstCommand = commands.shift();
            const fixedCommand = firstCommand.split('/').join('');
            commands.unshift(`/${OfficeRoute.Embed}/`.toLowerCase(), fixedCommand);
        }
        this.router.navigate(commands, { queryParamsHandling: 'preserve', replaceUrl: true });
    }

    public subscribeToRouteChangeStart(callback: (e: RouteChangeStartEvent) => void): Subscription {
        return this.routes.pipe(filter(this.isRouteChangeStartEvent)).subscribe(callback);
    }

    public subscribeToRouteChangeEnd(callback: (e: RouteChangeEndEvent) => void): Subscription {
        return this.routes.pipe(filter(this.isRouteChangeEndEvent)).subscribe(callback);
    }

    public getRouteChangeStartObservable(): Observable<RouteChangeStartEvent> {
        return this.routes.pipe(filter(this.isRouteChangeStartEvent));
    }

    public getRouteChangeEndObservable(): Observable<RouteChangeEndEvent> {
        return this.routes.pipe(filter(this.isRouteChangeEndEvent));
    }

    public isOnRoute(routeItem: OfficeRoute): boolean {
        return this.currentRouteState.routeItem.route === routeItem;
    }

    public getCurrentRoute(): OfficeRouteItem {
        return this.currentRouteState ? this.currentRouteState.routeItem : null;
    }

    public getCurrentPageForLogging(): string  {
        const route = this.getCurrentRoute();
        return route ? `${route.app ? FileType[route.app] : ''}${route.route}` : 'Unknown';
    }

    public getInitialRoute(): OfficeRouteItem {
        return this.initialRoute;
    }

    public getInitialPageForLogging(): string  {
        const route = this.initialRoute;
        return route ? `${route.app ? FileType[route.app] : ''}${route.route}` : 'Unknown';
    }

    public getRouteItemForCurrentLocation(): OfficeRouteItem {
        return this.currentRouteFromPath(this.platformLocation.pathname);
    }

    private isRouteChangeStartEvent(event: RouteChangeEvent): event is RouteChangeStartEvent {
        return event.type === RouteChangeEventType.NavigationStart;
    }

    private isRouteChangeEndEvent(event: RouteChangeEvent): event is RouteChangeEndEvent {
        return event.type === RouteChangeEventType.NavigationEnd;
    }

    private getCommandsAndParamsForRoute(route: OfficeRouteItem, additionalParams: { [x: string]: any } = null): [Array<string>, { [x: string]: any }] {
        if (!route) {
            return;
        }

        let queryParams = { ...this.activatedRoute.snapshot.queryParams };
        if (
            route.route !== OfficeRoute.Search &&
            this.currentRouteState &&
            this.currentRouteState.routeItem &&
            (this.currentRouteState.routeItem.route === OfficeRoute.Search ||
                this.currentRouteState.routeItem.route ===
                OfficeRoute.SearchPersonScoped)
        ) {
            delete queryParams['q']; // FAST Search Result Page search query parameter
            delete queryParams['p']; // FAST Search Result Page pagination parameter
            delete queryParams['e']; // FAST Search Result Page File Type parameter
            delete queryParams['s']; // FAST Search Result Page Site parameter
            delete queryParams['pp']; // FAST People Centric Search Parameter
            delete queryParams['refiners']; // FAST query refiners parameter
            delete queryParams['wm']; // FAST Search Result Page without modification query parameter
        }

        if (this.currentRouteState && this.currentRouteState.routeItem && this.currentRouteState.routeItem.route === OfficeRoute.Content) {
            delete queryParams['item'];
            delete queryParams['drive'];
            delete queryParams['file'];
            delete queryParams['action'];
            delete queryParams['siteUrl'];
            delete queryParams['abpId'];
            delete queryParams['t'];
        }

        if (additionalParams) {
            queryParams = { ...queryParams, ...additionalParams };
        }

        const commands: Array<string> = [];

        switch (route.route) {
            case OfficeRoute.AppGallery:
                commands.push(RoutePaths.AppGallery);
                break;
            case OfficeRoute.Search:
            case OfficeRoute.SearchSiteScoped:
                commands.push(RoutePaths.Search);
                break;
            case OfficeRoute.SearchPersonScoped:
                commands.push(RoutePaths.SearchPersonScoped);
                break;
            case OfficeRoute.Start:
                commands.push(this.capabilitiesService.capabilities.startPageRootPath);
                break;
            case OfficeRoute.ModernStart:
                commands.push(this.capabilitiesService.capabilities.modernStartPageRootPath);
                break;
            case OfficeRoute.Templates:
                commands.push(this.capabilitiesService.capabilities.templatePageRootPath);
                break;
            case OfficeRoute.Pwa:
                commands.push(RoutePaths.Pwa);
                break;
            case OfficeRoute.Create:
                commands.push(RoutePaths.Create);
                break;
            case OfficeRoute.Content:
            case OfficeRoute.MyForms:
            case OfficeRoute.GroupForms:
                // The route is nested under /launch/app/ route
                commands.push(this.capabilitiesService.capabilities.startPageRootPath);
                break;
            case OfficeRoute.M365Apps:
                // Add m365apps and appId as lowercase, Pass-through entityId/subEntityId
                commands.push(this.capabilitiesService.capabilities.m365AppsPageRootPath.toLowerCase());
                if (route.appId) {
                    commands.push(route.appId.toLowerCase());
                    if (route.entityId) {
                        commands.push(route.entityId);
                        if (route.subEntityId) {
                            commands.push(route.subEntityId);
                        }
                    }
                }
                break;
            case OfficeRoute.ConsumerAllSearch:
                commands.push(RoutePaths.ConsumerAllSearch);
                break;
            case OfficeRoute.ConsumerFileSearch:
                commands.push(RoutePaths.ConsumerFileSearch);
                break;
            case OfficeRoute.ConsumerTemplateSearch:
                commands.push(RoutePaths.ConsumerTemplateSearch);
                break;
            case OfficeRoute.MyContent:
                commands.push(RoutePaths.MyContent);
                break;
            case OfficeRoute.Feed:
                commands.push(RoutePaths.Feed);
                break;
            default:
                commands.push(RoutePaths.OfficeHomeFallback);
        }

        if (route.app) {
            commands.push(FileType[route.app]);
        }

        if (route.route === OfficeRoute.MyForms) {
            commands.push(OfficeRoute.MyForms);
        }

        if (route.route === OfficeRoute.GroupForms) {
            commands.push(OfficeRoute.GroupForms);
        }

        if (route.route === OfficeRoute.Content) {
            commands.push(this.capabilitiesService.capabilities.wacHostPagePath.replace('/', ''));
        }

        if (this.capabilitiesService.capabilities.isEmbedMode) {
            // The first command should have the '/' so we need to remove it from the old first command
            const firstCommand = commands.shift();
            const fixedCommand = firstCommand.split('/').join('');
            commands.unshift(`/${OfficeRoute.Embed}/`, fixedCommand);
        }

        // M365Apps route should respect casing of optional path params: entityId and subPageId
        if (route.route === OfficeRoute.M365Apps) {
            return [commands, queryParams];
        }

        return [commands.map((c: string) => c.toLowerCase()), queryParams];
    }

    private currentRouteFromPath(path: string): OfficeRouteItem {
        if (!path) {
            return null;
        }

        let lowerCasePath = path.toLowerCase();
        let route: OfficeRoute = null;
        let app: FileType = null;

        if (lowerCasePath.indexOf('/embed') === 0) {
            // Embed can be any route so we remove it from parsing
            lowerCasePath = lowerCasePath.replace('/embed', '');
        }

        if (lowerCasePath === '/' || lowerCasePath.indexOf('/home') === 0) {
            route = this.capabilitiesService.capabilities.pwaEnabled ? OfficeRoute.Pwa : OfficeRoute.OfficeHome;
        } else if (lowerCasePath.indexOf('/apps') === 0) {
            route = OfficeRoute.AppGallery;
        } else if (lowerCasePath.indexOf('/search') === 0) {
            if (this.isSiteScopedSearchRoute(lowerCasePath)) {
                route = OfficeRoute.SearchSiteScoped;
            } else if (this.isPersonScopedSearchRoute(lowerCasePath)) {
                route = OfficeRoute.SearchPersonScoped;
            } else {
                route = OfficeRoute.Search;
            }
        } else if (lowerCasePath.indexOf(`/${RoutePaths.Pwa}`) === 0) {
            route = OfficeRoute.Pwa;
        } else if (lowerCasePath.indexOf(`/${RoutePaths.M365Apps}`) === 0) {
            route = OfficeRoute.M365Apps;
            const pathSegments = path.split('/').filter(segment => !!segment);
            // Keep appId and m365apps lowercase
            const appId = pathSegments[1] ? pathSegments[1].toLowerCase() : undefined;
            // Pass through entityId/subEntityId as-is
            const entityId = pathSegments[2] ?? undefined;
            const subEntityId = pathSegments[3] ?? undefined;
            return {
                route,
                appId,
                entityId,
                subEntityId
            };
        } else if (lowerCasePath.indexOf(`/${RoutePaths.Create}`) === 0) {
            route = OfficeRoute.Create;
        } else if (lowerCasePath.indexOf(`/${RoutePaths.MyContent}`) === 0) {
            route = OfficeRoute.MyContent;
        } else if (lowerCasePath.indexOf(`/${RoutePaths.Feed}`) === 0) {
            route = OfficeRoute.Feed;
        } else {
            const isTemplatesPage = lowerCasePath.indexOf(this.capabilitiesService.capabilities.templatePageRootPath.toLowerCase()) === 0;
            const isStartPage = lowerCasePath.indexOf(this.capabilitiesService.capabilities.startPageRootPath.toLowerCase()) === 0;
            const isModernStartPage = lowerCasePath.indexOf(this.capabilitiesService.capabilities.modernStartPageRootPath.toLowerCase()) === 0;
            const isWacHostPage = lowerCasePath.indexOf(this.capabilitiesService.capabilities.wacHostPagePath.toLowerCase()) >= 0 && (isStartPage || isModernStartPage);
            const isMyFormsPage = lowerCasePath.toLocaleLowerCase().indexOf('/myforms') >= 0 && isStartPage;
            const isGroupFormsPage = lowerCasePath.toLocaleLowerCase().indexOf('/groupforms') >= 0 && isStartPage;
            if (isTemplatesPage || isStartPage || isModernStartPage || isWacHostPage || isMyFormsPage || isGroupFormsPage) {
                const splitPath = lowerCasePath.split('/');
                const appName = splitPath[isWacHostPage || isMyFormsPage || isGroupFormsPage ? splitPath.length - 2 : splitPath.length - 1];
                route = isTemplatesPage ? OfficeRoute.Templates : isWacHostPage ? OfficeRoute.Content : isMyFormsPage ? OfficeRoute.MyForms : isGroupFormsPage ? OfficeRoute.GroupForms : isModernStartPage ? OfficeRoute.ModernStart : OfficeRoute.Start;
                app = FileItem.fileTypeNameToFileType(appName);
            }
        }

        return route ? {
            route,
            app
        } : null;
    }

    private navigateToInternal(commands: Array<string>, replace: boolean = false, queryParams: { [x: string]: any } = {}, localStorageKey?: string, eagerLoad: boolean = false): Promise<boolean> {
        if (replace) {
            this.currentRouteState = this.previousRouteState;
            this.rewriteInProgress = true;
        }

        if (localStorageKey && commands.length) {
            this.localStorageService.set(localStorageKey, commands[0]);
        }

        if (eagerLoad) {
            this.router.urlUpdateStrategy = 'eager';
        } else {
            this.router.urlUpdateStrategy = 'deferred';
        }

        return this.router.navigate(commands, { queryParams, replaceUrl: replace });
    }

    private parseState(state: any): OfficeRouteHistoryEntry {
        const officeState: OfficeRouteHistoryEntry = state ? state.ohpState : null;
        if (officeState && officeState.version !== OfficeRouteService.officeRouteStateVersion) {
            this.versionMismatch = true;
        }
        return this.versionMismatch ? null : officeState;
    }

    private updateState(ohpState: OfficeRouteHistoryEntry) {
        if (this.historyEnabled) {
            const historyState = this.windowService.nativeWindow.history.state ? { ...this.windowService.nativeWindow.history.state } : {};
            historyState.ohpState = ohpState;
            // Url must be provided for angular router to play nicely with Safari
            this.locationStrategy.replaceState(historyState, '', this.platformLocation.pathname, this.platformLocation.search + this.platformLocation.hash);
        }
    }

    private isSiteScopedSearchRoute(path: string): boolean {
        return path.startsWith('/search/siteall') ||
            path.startsWith('/search/sitenews') ||
            path.startsWith('/search/sitesites') ||
            path.startsWith('/search/sitefiles') ||
            path.startsWith('/search/siteimages');
    }

    private isPersonScopedSearchRoute(path: string): boolean {
        return path.startsWith('/search/content') ||
            path.startsWith('/search/contact') ||
            path.startsWith('/search/organization') ||
            path.startsWith('/search/calendar') ||
            path.startsWith('/search/office');
    }
}
